import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import { COMMENT_AND_FILE_MODE } from "../features/customers/commentsAndFiles/commentsAndFilesConstants";
import {
    calculatePagination,
    mapApiDataToTableFormat,
} from "../features/customers/commentsAndFiles/commentsAndFilesUtils";
import toast from "../hooks/toast";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../shared/Utils";
import { deduplicateRequest } from "../shared/services/apiDeduplicationService";

// Module-level promise for deduplication
let fetchCustomerCommentsPromise = null;

// API functions
const getCustomerComments = async ({ customerId, top = 10, skip = 0 }) => {
    const filter = "";
    const include = "";
    const sortColumn = "";
    return await axios.get(
        buildApiV3UrlWithQuery(
            `customers/${customerId}/customer-comments`,
            filter,
            include,
            top,
            skip,
            sortColumn
        ),
        { withCredentials: true }
    );
};

const addCustomerComment = async ({ customerId, commentData }) => {
    return await axios.post(
        buildApiV3Url(`customers/${customerId}/customer-comments`),
        commentData,
        {
            withCredentials: true,
        }
    );
};

const updateCustomerComment = async ({ customerId, commentId, commentData }) => {
    return await axios.put(
        buildApiV3Url(`customers/${customerId}/customer-comments/${commentId}`),
        commentData,
        {
            withCredentials: true,
        }
    );
};

const deleteCustomerComment = async ({ customerId, commentId }) => {
    return await axios.delete(
        buildApiV3Url(`customers/${customerId}/customer-comments/${commentId}`),
        {
            withCredentials: true,
        }
    );
};

export const getCustomerCommentFile = async ({ customerId, customerCommentId }) => {
    return await axios.get(
        buildApiV3Url(`customers/${customerId}/customer-comments/${customerCommentId}/file`),
        {
            withCredentials: true,
            responseType: "blob",
        }
    );
};

const initialState = {
    isLoading: false,
    isSaving: false,
    isDeleting: false,
    comments: {
        data: [],
        totalRecords: 0,
    },
    // Dialog state
    isAddDialogOpen: false,
    mode: COMMENT_AND_FILE_MODE.ADD,
    currentComment: "",
    currentCommentId: "",
    currentFileName: "",
    // Pagination state
    currentPage: 1,
    rowsPerPage: 10,
    startIndex: 0,
    // Current customer
    customerId: null,
};

// Async thunks
export const fetchCustomerCommentsAsync = createAsyncThunk(
    "customerComments/fetchComments",
    async ({ customerId, currentPage = 1, rowsPerPage = 10 }, { rejectWithValue }) => {
        try {
            const { top, skip } = calculatePagination(currentPage, rowsPerPage);

            // Use deduplication to prevent concurrent duplicate calls
            fetchCustomerCommentsPromise = deduplicateRequest(
                fetchCustomerCommentsPromise,
                async () => {
                    const response = await getCustomerComments({ customerId, top, skip });
                    // Clear the promise after successful completion
                    fetchCustomerCommentsPromise = null;
                    return response;
                }
            );

            const { data } = await fetchCustomerCommentsPromise;
            const { value: commentsData, count } = data || {};
            return {
                data: mapApiDataToTableFormat(commentsData),
                totalRecords: count,
                currentPage,
                rowsPerPage,
            };
        } catch (error) {
            // Clear the promise on error
            fetchCustomerCommentsPromise = null;
            return rejectWithValue(error?.message || "Failed to fetch customer comments");
        }
    }
);

export const saveCustomerCommentAsync = createAsyncThunk(
    "customerComments/saveComment",
    async ({ customerId, mode, comment, file, currentCommentId }, { rejectWithValue }) => {
        try {
            const [showToast] = toast();
            const commentData = {
                commentEntry: comment,
                fileEntry: file,
            };

            let apiResponse;
            if (mode === COMMENT_AND_FILE_MODE.ADD) {
                apiResponse = await addCustomerComment({ customerId, commentData });
            } else {
                apiResponse = await updateCustomerComment({
                    customerId,
                    commentId: currentCommentId,
                    commentData,
                });
            }

            const { data } = apiResponse;
            const { success, message } = data || {};

            if (success) {
                showToast("success", message);
                return { success: true, customerId };
            } else {
                showToast("error", message);
                return rejectWithValue(message || "Failed to save customer comment");
            }
        } catch (error) {
            const [showToast] = toast();
            showToast("error", error.message);
            return rejectWithValue(error?.message || "Failed to save customer comment");
        }
    }
);

export const deleteCustomerCommentAsync = createAsyncThunk(
    "customerComments/deleteComment",
    async ({ customerId, commentId }, { rejectWithValue }) => {
        try {
            const [showToast] = toast();
            const { data } = await deleteCustomerComment({ customerId, commentId });
            const { success, message } = data || {};

            if (success) {
                showToast("success", message);
                return { success: true, customerId };
            } else {
                showToast("error", message);
                return rejectWithValue(message || "Failed to delete customer comment");
            }
        } catch (error) {
            const [showToast] = toast();
            showToast("error", error.message);
            return rejectWithValue(error?.message || "Failed to delete customer comment");
        }
    }
);

export const customerCommentsSlice = createSlice({
    name: "customerComments",
    initialState,
    reducers: {
        setCustomerId: (state, action) => {
            state.customerId = action.payload;
        },
        openAddDialog: state => {
            state.isAddDialogOpen = true;
            state.mode = COMMENT_AND_FILE_MODE.ADD;
            state.currentComment = "";
            state.currentCommentId = "";
            state.currentFileName = "";
        },
        openEditDialog: (state, action) => {
            state.isAddDialogOpen = true;
            state.mode = COMMENT_AND_FILE_MODE.EDIT;
            state.currentComment = action.payload.comment;
            state.currentCommentId = action.payload.commentId;
            state.currentFileName = action.payload.file || "";
        },
        closeDialog: state => {
            state.isAddDialogOpen = false;
        },
        setDeleteDialogVisibility: (state, action) => {
            state.isDeleteConfirmationDialogOpen = action.payload.isDeleteConfirmationDialogOpen;
        },
        setPagination: (state, action) => {
            state.currentPage = action.payload.page;
            state.rowsPerPage = action.payload.rowsPerPage;
            state.startIndex = (action.payload.page - 1) * action.payload.rowsPerPage;
        },
        resetState: () => {
            return { ...initialState };
        },
    },
    extraReducers: builder => {
        builder
            // Fetch comments
            .addCase(fetchCustomerCommentsAsync.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchCustomerCommentsAsync.fulfilled, (state, action) => {
                state.isLoading = false;
                state.comments = {
                    data: action.payload.data,
                    totalRecords: action.payload.totalRecords,
                };
                state.currentPage = action.payload.currentPage;
                state.rowsPerPage = action.payload.rowsPerPage;
                state.startIndex = (action.payload.currentPage - 1) * action.payload.rowsPerPage;
            })
            .addCase(fetchCustomerCommentsAsync.rejected, (state, action) => {
                state.isLoading = false;
                state.comments = { data: [], totalRecords: 0 };
                const [showToast] = toast();
                showToast("error", action.payload || "Failed to fetch comments");
            })
            // Save comment
            .addCase(saveCustomerCommentAsync.pending, state => {
                state.isSaving = true;
            })
            .addCase(saveCustomerCommentAsync.fulfilled, state => {
                state.isSaving = false;
                state.isAddDialogOpen = false;
                // Reset pagination to first page after save
                state.currentPage = 1;
                state.startIndex = 0;
            })
            .addCase(saveCustomerCommentAsync.rejected, state => {
                state.isSaving = false;
            })
            // Delete comment
            .addCase(deleteCustomerCommentAsync.pending, state => {
                state.isDeleting = true;
            })
            .addCase(deleteCustomerCommentAsync.fulfilled, state => {
                state.isDeleting = false;
                // Reset pagination to first page after delete
                state.currentPage = 1;
                state.startIndex = 0;
                state.isDeleteConfirmationDialogOpen = false;
            })
            .addCase(deleteCustomerCommentAsync.rejected, state => {
                state.isDeleting = false;
            });
    },
});

export const {
    setCustomerId,
    openAddDialog,
    openEditDialog,
    closeDialog,
    setPagination,
    resetState,
    setDeleteDialogVisibility,
} = customerCommentsSlice.actions;

// Selectors
export const selectCustomerComments = state => state.customerComments.comments;
export const selectIsLoading = state => state.customerComments.isLoading;
export const selectIsSaving = state => state.customerComments.isSaving;
export const selectIsDeleting = state => state.customerComments.isDeleting;
export const selectIsAddDialogOpen = state => state.customerComments.isAddDialogOpen;
export const selectMode = state => state.customerComments.mode;
export const selectCurrentComment = state => state.customerComments.currentComment;
export const selectCurrentCommentId = state => state.customerComments.currentCommentId;
export const selectCurrentFileName = state => state.customerComments.currentFileName;
export const selectCurrentPage = state => state.customerComments.currentPage;
export const selectRowsPerPage = state => state.customerComments.rowsPerPage;
export const selectStartIndex = state => state.customerComments.startIndex;
export const selectCustomerId = state => state.customerComments.customerId;
export const selectIsDeleteConfirmationDialogOpen = state =>
    state.customerComments.isDeleteConfirmationDialogOpen;
export default customerCommentsSlice.reducer;
